import React, { useState, useEffect, useCallback, useRef } from "react";
import { Crop, Move, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

// Step 1: License Crop Component - Crop the license from the photo
export const LicenseCropTool = ({ image, onConfirm, onCancel }) => {
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [cropArea, setCropArea] = useState({ x: 10, y: 10, width: 80, height: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const handleImageLoad = (e) => {
    setImageDimensions({ width: e.target.naturalWidth, height: e.target.naturalHeight });
    setImageLoaded(true);
    setCropArea({ x: 10, y: 10, width: 80, height: 80 });
  };
  
  const getEventPosition = (e) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100
    };
  };
  
  const handleMouseDown = (e, handle = null) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getEventPosition(e);
    setDragStart({ x: pos.x - cropArea.x, y: pos.y - cropArea.y });
    
    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
    } else {
      setIsDragging(true);
    }
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    
    const pos = getEventPosition(e);
    
    if (isDragging) {
      const newX = Math.max(0, Math.min(100 - cropArea.width, pos.x - dragStart.x));
      const newY = Math.max(0, Math.min(100 - cropArea.height, pos.y - dragStart.y));
      setCropArea(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      setCropArea(prev => {
        let { x, y, width, height } = prev;
        
        switch (resizeHandle) {
          case 'se':
            width = Math.max(20, Math.min(100 - x, pos.x - x));
            height = Math.max(20, Math.min(100 - y, pos.y - y));
            break;
          case 'sw':
            const newWidthSW = Math.max(20, x + width - pos.x);
            x = Math.max(0, Math.min(x + width - 20, pos.x));
            width = newWidthSW;
            height = Math.max(20, Math.min(100 - y, pos.y - y));
            break;
          case 'ne':
            width = Math.max(20, Math.min(100 - x, pos.x - x));
            const newHeightNE = Math.max(20, y + height - pos.y);
            y = Math.max(0, Math.min(y + height - 20, pos.y));
            height = newHeightNE;
            break;
          case 'nw':
            const newWidthNW = Math.max(20, x + width - pos.x);
            const newHeightNW = Math.max(20, y + height - pos.y);
            x = Math.max(0, Math.min(x + width - 20, pos.x));
            y = Math.max(0, Math.min(y + height - 20, pos.y));
            width = newWidthNW;
            height = newHeightNW;
            break;
          default:
            break;
        }
        
        return { x, y, width, height };
      });
    }
  }, [isDragging, isResizing, dragStart, resizeHandle, cropArea.width, cropArea.height]);
  
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };
  
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove]);
  
  const handleConfirm = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const cropX = (cropArea.x / 100) * img.width;
      const cropY = (cropArea.y / 100) * img.height;
      const cropW = (cropArea.width / 100) * img.width;
      const cropH = (cropArea.height / 100) * img.height;
      
      canvas.width = cropW;
      canvas.height = cropH;
      
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      
      const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
      onConfirm(croppedImage);
    };
    
    img.src = image;
  };
  
  return (
    <div className="fixed inset-0 bg-black z-[9999] flex flex-col sm:items-center sm:justify-center sm:p-4 sm:bg-black/90">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-xl sm:max-w-2xl overflow-hidden shadow-2xl flex flex-col">
        <div className="p-4 border-b bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-center gap-2">
            <Crop className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-lg">Step 1: Crop Driver License</h3>
          </div>
          <p className="text-sm text-slate-500 text-center mt-1">Drag the corners to crop around the license</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div 
            ref={containerRef}
            className="relative w-full bg-slate-900 rounded-lg overflow-hidden select-none"
            style={{ aspectRatio: imageDimensions.width && imageDimensions.height ? `${imageDimensions.width}/${imageDimensions.height}` : '16/10' }}
          >
            <img 
              src={image} 
              alt="Original" 
              className="w-full h-full object-contain"
              onLoad={handleImageLoad}
              draggable={false}
            />
            
            {imageLoaded && (
              <>
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute bg-black/60" style={{ top: 0, left: 0, right: 0, height: `${cropArea.y}%` }} />
                  <div className="absolute bg-black/60" style={{ bottom: 0, left: 0, right: 0, height: `${100 - cropArea.y - cropArea.height}%` }} />
                  <div className="absolute bg-black/60" style={{ top: `${cropArea.y}%`, left: 0, width: `${cropArea.x}%`, height: `${cropArea.height}%` }} />
                  <div className="absolute bg-black/60" style={{ top: `${cropArea.y}%`, right: 0, width: `${100 - cropArea.x - cropArea.width}%`, height: `${cropArea.height}%` }} />
                </div>
                
                <div 
                  className="absolute border-2 border-blue-400 cursor-move"
                  style={{
                    left: `${cropArea.x}%`,
                    top: `${cropArea.y}%`,
                    width: `${cropArea.width}%`,
                    height: `${cropArea.height}%`,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0)'
                  }}
                  onMouseDown={(e) => handleMouseDown(e)}
                  onTouchStart={(e) => handleMouseDown(e)}
                >
                  {['nw', 'ne', 'sw', 'se'].map(handle => (
                    <div
                      key={handle}
                      className="absolute w-6 h-6 bg-blue-500 border-2 border-white rounded-sm cursor-pointer"
                      style={{
                        top: handle.includes('n') ? -8 : 'auto',
                        bottom: handle.includes('s') ? -8 : 'auto',
                        left: handle.includes('w') ? -8 : 'auto',
                        right: handle.includes('e') ? -8 : 'auto',
                        cursor: handle === 'nw' || handle === 'se' ? 'nwse-resize' : 'nesw-resize'
                      }}
                      onMouseDown={(e) => handleMouseDown(e, handle)}
                      onTouchStart={(e) => handleMouseDown(e, handle)}
                    />
                  ))}
                  
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/3 left-0 right-0 border-t border-white/30" />
                    <div className="absolute top-2/3 left-0 right-0 border-t border-white/30" />
                    <div className="absolute left-1/3 top-0 bottom-0 border-l border-white/30" />
                    <div className="absolute left-2/3 top-0 bottom-0 border-l border-white/30" />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <p className="text-xs text-center text-slate-500 mt-3">
            Crop the image to show only the driver license
          </p>
        </div>
        
        <div className="p-4 border-t bg-slate-50 flex gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="flex-1 bg-blue-600 hover:bg-blue-700">
            Next: Select Face
          </Button>
        </div>
      </div>
    </div>
  );
};

// Step 2: Face Selection Component
export const ImageCropSelector = ({ image, onConfirm, onCancel, initialPosition }) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [position, setPosition] = useState(initialPosition || { x: 25, y: 30 });
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(initialPosition?.zoom || 2);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPosition({
      x: Math.max(10, Math.min(90, x)),
      y: Math.max(10, Math.min(90, y))
    });
  }, [isDragging]);
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setPosition({
      x: Math.max(10, Math.min(90, x)),
      y: Math.max(10, Math.min(90, y))
    });
  }, [isDragging]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);
  
  return (
    <div className="fixed inset-0 bg-black z-[9999] flex flex-col sm:items-center sm:justify-center sm:p-4 sm:bg-black/90">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-xl sm:max-w-lg overflow-hidden shadow-2xl flex flex-col">
        <div className="p-4 border-b bg-slate-50 flex-shrink-0">
          <h3 className="font-semibold text-lg text-center">Select Face Area</h3>
          <p className="text-sm text-slate-500 text-center mt-1">Drag the circle to position the face</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div 
            ref={containerRef}
            className="relative w-full aspect-[1.6/1] bg-slate-900 rounded-lg overflow-hidden cursor-crosshair select-none"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <img 
              ref={imageRef}
              src={image} 
              alt="Driver License" 
              className="w-full h-full object-contain pointer-events-none"
              draggable={false}
              onLoad={() => setImageLoaded(true)}
            />
            
            {imageLoaded && (
              <>
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle 45px at ${position.x}% ${position.y}%, transparent 0%, transparent 45px, rgba(0,0,0,0.7) 46px)`
                  }}
                />
                
                <div 
                  className="absolute w-[90px] h-[90px] border-4 border-blue-400 rounded-full shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    boxShadow: '0 0 0 2px white, 0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <Move className="w-5 h-5 text-blue-400 drop-shadow-lg" />
                </div>
              </>
            )}
          </div>
          
          <div className="mt-4">
            <Label className="text-sm text-slate-600">Avatar Zoom: {zoom.toFixed(1)}x</Label>
            <input 
              type="range" 
              min="1.2" 
              max="3.5" 
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full mt-1 accent-blue-600"
            />
          </div>
          
          <div className="mt-4 flex items-center justify-center">
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-2">Avatar Preview</p>
              <div 
                className="w-20 h-20 rounded-full border-4 border-slate-200 shadow-lg mx-auto"
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundSize: `${zoom * 100}%`,
                  backgroundPosition: `${position.x}% ${position.y}%`,
                  backgroundRepeat: 'no-repeat'
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t bg-slate-50 flex gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={() => onConfirm({ x: position.x, y: position.y, zoom })} className="flex-1 bg-blue-600 hover:bg-blue-700">
            <Check className="w-4 h-4 mr-2" />
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

// Driver License Lightbox Component
export const DriverLicenseLightbox = ({ photo, isOpen, onClose }) => {
  if (!isOpen || !photo) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        <img 
          src={photo} 
          alt="Driver License" 
          className="w-full rounded-lg shadow-2xl"
        />
        <p className="text-center text-white/70 text-sm mt-3">Click outside or X to close</p>
      </div>
    </div>
  );
};
